/**
 * Shared checklist item deduplication utility.
 *
 * Problem: When the "Create default checklist" button was clicked with
 * force=true before the edge function was made idempotent, duplicate
 * checklist items were created. Some duplicates have the same
 * source_template_item_id (from the template), while others have
 * source_template_item_id = NULL (created by a different code path).
 *
 * Strategy:
 * 1. Items WITH source_template_item_id are deduplicated by that ID.
 *    Keep the first occurrence.
 * 2. Items WITHOUT source_template_item_id are deduplicated by a
 *    composite key: title + checklist_id + requires_management_check +
 *    requires_subcontractor_check.
 *    Keep the first occurrence.
 *
 * This prevents double-counting regardless of how duplicates were created.
 */

export interface DedupableChecklistItem {
  id: string;
  title: string;
  checklist_id?: string | null;
  requires_management_check?: boolean;
  requires_subcontractor_check?: boolean;
  source_template_item_id?: string | null;
}

export function deduplicateChecklistItems<T extends DedupableChecklistItem>(
  items: T[]
): { unique: T[]; duplicates: T[] } {
  const seenTemplateIds = new Set<string>();
  const seenNullKeys = new Set<string>();
  const unique: T[] = [];
  const duplicates: T[] = [];

  for (const item of items) {
    if (item.source_template_item_id) {
      // Deduplicate by template ID
      if (seenTemplateIds.has(item.source_template_item_id)) {
        duplicates.push(item);
      } else {
        seenTemplateIds.add(item.source_template_item_id);
        unique.push(item);
      }
    } else {
      // Deduplicate by composite key for items without template ID
      const key = [
        item.title,
        item.checklist_id || '',
        item.requires_management_check !== false ? 'mgmt' : 'no-mgmt',
        item.requires_subcontractor_check === true ? 'sub' : 'no-sub',
      ].join('::');
      if (seenNullKeys.has(key)) {
        duplicates.push(item);
      } else {
        seenNullKeys.add(key);
        unique.push(item);
      }
    }
  }

  return { unique, duplicates };
}